import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/api/client';
import type { Job } from '@/api/types';
import {
  formatJobType,
  jobStatusLabel,
  jobStatusVariant,
} from '@/utils/jobStatus';
import StatusBadge from '@/components/ui/StatusBadge';

type Props = {
  searchId?: string;
  subscriptionId?: string;
  limit?: number;
  onSelectJob?: (jobId: string) => void;
  title?: string;
};

function formatTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export default function RecentJobsPanel({
  searchId,
  subscriptionId,
  limit = 5,
  onSelectJob,
  title = 'Recent jobs',
}: Props) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!searchId && !subscriptionId) {
      setJobs([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await api.jobs.list({
        search_id: searchId,
        subscription_id: subscriptionId,
        limit,
      });
      setJobs(rows);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [searchId, subscriptionId, limit]);

  useEffect(() => {
    load();
  }, [load]);

  if (!searchId && !subscriptionId) return null;

  return (
    <section className="card section recent-jobs-panel">
      <header className="recent-jobs-panel__header">
        <h3 className="recent-jobs-panel__title">{title}</h3>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => load()}>
          Refresh
        </button>
      </header>

      {loading && jobs.length === 0 && <p className="muted">Loading recent jobs…</p>}
      {error && <p className="error-text">{error}</p>}

      {!loading && jobs.length === 0 && !error && (
        <p className="muted">No scrape jobs yet for this {searchId ? 'search' : 'subscription'}.</p>
      )}

      {jobs.length > 0 && (
        <ul className="recent-jobs-list">
          {jobs.map((job) => (
            <li key={job.id} className="recent-jobs-list__item">
              <div className="recent-jobs-list__main">
                <span className="recent-jobs-list__type">{formatJobType(job.type)}</span>
                <StatusBadge variant={jobStatusVariant(job.status)}>
                  {jobStatusLabel(job.status)}
                </StatusBadge>
              </div>
              <div className="recent-jobs-list__meta muted">
                {job.processed_items}/{job.total_items} processed
                {job.failed_items > 0 && ` · ${job.failed_items} failed`}
                {' · '}
                {formatTime(job.created_at)}
              </div>
              {onSelectJob && (
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => onSelectJob(job.id)}>
                  View progress
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
